import { useEffect, useState, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Loader2,
  RefreshCw,
  Download,
  Search,
  Trash2,
  Mail,
  Sparkles,
  Inbox,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pricingLeads } from '@/lib/api';

const PLAN_LABEL = {
  'head-start': 'Head Start',
  primary: 'Primary',
  intermediate: 'Intermediate',
  enterprise: 'Collegiate',
  any: 'Any / Undecided',
};

const PLAN_BADGE_CLASS = {
  'head-start': 'bg-slate-600 text-white',
  primary: 'bg-emerald-600 text-white',
  intermediate: 'bg-amber-500 text-slate-900',
  enterprise: 'bg-indigo-600 text-white',
  any: 'bg-slate-500 text-white',
};

function formatDateSafe(value) {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? parseISO(value) : new Date(value);
    return format(d, 'MMM d, yyyy h:mm a');
  } catch (_e) {
    return String(value);
  }
}

function buildCSV(leads) {
  const headers = [
    'email',
    'preferred_plan',
    'source',
    'submit_count',
    'created_at',
    'last_updated_at',
    'ip',
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const rows = leads.map((l) => headers.map((h) => escape(l[h])).join(','));
  return [headers.join(','), ...rows].join('\n');
}

function downloadCSV(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function PricingLeadsTab() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchLeads = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await pricingLeads.list({ limit: 500 });
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch (_e) {
      toast.error('Failed to load pricing leads');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(
      (l) =>
        l.email?.toLowerCase().includes(q) ||
        l.preferred_plan?.toLowerCase().includes(q) ||
        l.source?.toLowerCase().includes(q)
    );
  }, [leads, searchQuery]);

  const stats = useMemo(() => {
    const byPlan = leads.reduce((acc, l) => {
      const key = l.preferred_plan || 'any';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return { byPlan };
  }, [leads]);

  const handleExportCSV = () => {
    if (filtered.length === 0) {
      toast.error('No leads to export');
      return;
    }
    const csv = buildCSV(filtered);
    const stamp = format(new Date(), 'yyyy-MM-dd_HH-mm');
    downloadCSV(csv, `pricing-leads_${stamp}.csv`);
    toast.success(`Exported ${filtered.length} lead${filtered.length === 1 ? '' : 's'} to CSV`);
  };

  const handleDelete = async () => {
    if (!leadToDelete) return;
    setIsDeleting(true);
    try {
      await pricingLeads.remove(leadToDelete.lead_id);
      toast.success(`Removed ${leadToDelete.email}`);
      setLeadToDelete(null);
      fetchLeads();
    } catch (_e) {
      toast.error('Failed to delete lead');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="pricing-leads-tab">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by email, plan, or source…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
              data-testid="pricing-leads-search-input"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLeads}
            className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
            data-testid="pricing-leads-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleExportCSV}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            data-testid="pricing-leads-export-csv-btn"
            disabled={filtered.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV ({filtered.length})
          </Button>
        </div>
      </div>

      {/* Plan breakdown chips */}
      {leads.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-400">Plan interest:</span>
          {Object.entries(stats.byPlan).map(([planId, count]) => (
            <Badge
              key={planId}
              className={`${PLAN_BADGE_CLASS[planId] || 'bg-slate-600 text-white'} font-normal`}
            >
              {PLAN_LABEL[planId] || planId}: {count}
            </Badge>
          ))}
          <span className="text-slate-500 ml-1">— {total} total</span>
        </div>
      )}

      {/* Table card */}
      <Card className="bg-slate-800 border-slate-700">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <div className="h-14 w-14 rounded-full bg-slate-700 text-slate-400 flex items-center justify-center mb-4">
              <Inbox className="h-7 w-7" />
            </div>
            <h3 className="text-lg font-semibold text-white">No pricing leads yet</h3>
            <p className="text-sm text-slate-400 mt-1 max-w-md">
              When visitors opt in via the &ldquo;Notify me when paid plans launch&rdquo; form on the
              Pricing page, they will appear here.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <Search className="h-10 w-10 text-slate-500 mb-3" />
            <p className="text-sm text-slate-400">No leads match your search.</p>
          </div>
        ) : (
          <ScrollArea className="h-[520px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-900/95 backdrop-blur z-10">
                <tr className="text-left text-slate-400">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Preferred Plan</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 font-medium text-center">Submits</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium">Last Updated</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {filtered.map((lead) => (
                  <tr
                    key={lead.lead_id}
                    className="hover:bg-slate-750"
                    data-testid={`pricing-lead-row-${lead.lead_id}`}
                  >
                    <td className="px-4 py-3">
                      <a
                        href={`mailto:${lead.email}`}
                        className="text-emerald-400 hover:underline flex items-center gap-2"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        {lead.email}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      {lead.preferred_plan ? (
                        <Badge
                          className={`${PLAN_BADGE_CLASS[lead.preferred_plan] || 'bg-slate-600 text-white'} font-normal`}
                        >
                          {PLAN_LABEL[lead.preferred_plan] || lead.preferred_plan}
                        </Badge>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      <span className="inline-flex items-center gap-1.5">
                        <Sparkles className="h-3 w-3 text-slate-500" />
                        {lead.source || 'unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-300">
                      {lead.submit_count || 1}
                    </td>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                      {formatDateSafe(lead.created_at)}
                    </td>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                      {formatDateSafe(lead.last_updated_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                        onClick={() => setLeadToDelete(lead)}
                        data-testid={`pricing-lead-delete-${lead.lead_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </Card>

      {/* Delete confirmation */}
      <AlertDialog open={!!leadToDelete} onOpenChange={(open) => !open && setLeadToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this pricing lead?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-slate-900">{leadToDelete?.email}</span> will be
              removed from the launch-notification list. They can re-subscribe at any time from the
              Pricing page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="pricing-lead-confirm-delete-btn"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Remove lead
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
